import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

function CampaignModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ name: "", description: "", start_date: "", end_date: "", status: "active", color: "#D4AF37" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    if (editing) setForm({ ...editing });
    else setForm({ name: "", description: "", start_date: "", end_date: "", status: "active", color: "#D4AF37" });
  }, [open, editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.start_date || !form.end_date) { setErr("Name and dates required"); return; }
    if (form.end_date < form.start_date) { setErr("End date must be on/after start"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/campaigns/${editing.id}`, form);
        toast.success("Campaign updated");
      } else {
        await api.post("/campaigns", form);
        toast.success("Campaign created");
      }
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="campaign-modal">
      <div className="w-full max-w-md iz-card p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">{editing ? "Edit" : "New"} Campaign</p>
        <h2 className="font-display text-2xl text-white mb-6">{editing ? "Refine the plan" : "Create a campaign"}</h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Name</label>
            <input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} className="iz-input" data-testid="campaign-name" required/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Description</label>
            <textarea value={form.description || ""} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} className="iz-input" rows={3} data-testid="campaign-desc"/>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Start</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm(f => ({...f, start_date: e.target.value}))} className="iz-input" required data-testid="campaign-start"/>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">End</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm(f => ({...f, end_date: e.target.value}))} className="iz-input" required data-testid="campaign-end"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Status</label>
              <select value={form.status} onChange={(e) => setForm(f => ({...f, status: e.target.value}))} className="iz-input" data-testid="campaign-status">
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Color</label>
              <input type="color" value={form.color} onChange={(e) => setForm(f => ({...f, color: e.target.value}))} className="iz-input h-10 p-1"/>
            </div>
          </div>
          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary" data-testid="campaign-submit">{saving ? "Saving…" : (editing ? "Save" : "Create Campaign")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await api.delete(`/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 flex items-center justify-between bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Marketing · Manager</p>
          <h1 className="font-display text-3xl text-white mt-1">Campaigns</h1>
        </div>
        <button className="iz-btn-primary flex items-center gap-2" onClick={() => setModal({ open: true, editing: null })} data-testid="create-campaign-button">
          <Plus className="w-4 h-4"/> Create Campaign
        </button>
      </div>

      <div className="px-10 py-8">
        {loading ? (
          <div className="text-center text-gray-500 py-20">Loading campaigns…</div>
        ) : campaigns.length === 0 ? (
          <div className="iz-card p-12 text-center">
            <p className="text-gray-500 text-sm">No campaigns yet.</p>
            <button className="iz-btn-primary mt-4" onClick={() => setModal({ open: true, editing: null })}>Create your first campaign</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns.map(c => (
              <div key={c.id} className="iz-card p-5 hover:border-[#d4af37]/40 transition-colors" data-testid={`campaign-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: c.color || "#d4af37" }}/>
                      <span className={`text-[9px] font-mono uppercase tracking-[0.25em] ${c.status === "active" ? "text-[#d4af37]" : "text-gray-500"}`}>{c.status}</span>
                    </div>
                    <p className="font-display text-lg text-white leading-tight">{c.name}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setModal({ open: true, editing: c })} className="text-xs text-gray-400 hover:text-[#d4af37] px-2 py-1" data-testid={`campaign-edit-${c.id}`}>Edit</button>
                    <button onClick={() => remove(c)} className="p-1.5 rounded hover:bg-red-500/10 text-gray-500 hover:text-red-400" data-testid={`campaign-delete-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                </div>
                {c.description && <p className="text-xs text-gray-500 mt-3 leading-relaxed">{c.description}</p>}
                <div className="mt-4 flex items-center justify-between pt-3 border-t border-[#1a1a1a] text-[10px] font-mono uppercase tracking-widest text-gray-500">
                  <span>{c.start_date}</span>
                  <span className="text-[#d4af37]">→</span>
                  <span>{c.end_date}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CampaignModal
        open={modal.open}
        editing={modal.editing}
        onClose={() => setModal({ open: false, editing: null })}
        onSaved={() => { setModal({ open: false, editing: null }); load(); }}
      />
    </div>
  );
}
